import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Sparkles, Star, Plus, Trash2, X, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { EchoMapSkeleton } from '@/components/Skeletons';

// Milky Way band — themes are plotted along a soft S-curve. Each theme's
// position is deterministic (based on its rank in the frequency-sorted
// list) so the map is stable across renders.
// x sweeps left→right; y follows a gentle sinusoidal curve with small
// deterministic jitter derived from the theme label.
const milkyWayPositionFor = (rank, total, themeLabel) => {
  const n = Math.max(1, total);
  const t = n === 1 ? 0.5 : rank / (n - 1); // 0..1
  const x = 8 + t * 84; // 8-92%
  // Base curve: gentle S sweep across the sky.
  const base = 50 + Math.sin(t * Math.PI * 1.4 - 0.4) * 16;
  // Deterministic jitter so stars aren't perfectly on the line.
  let h = 0;
  const s = String(themeLabel || '');
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  const jitter = ((Math.abs(h) % 100) / 100 - 0.5) * 14; // ±7%
  const y = Math.max(10, Math.min(85, base + jitter));
  return { x, y };
};

export default function EchoMapPage() {
  const { user, checkAuth } = useAuth();
  const [glimmers, setGlimmers] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [event, setEvent] = useState('');
  const [score, setScore] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [horizon, setHorizon] = useState('');
  const [editingHorizon, setEditingHorizon] = useState(false);
  const [savingHorizon, setSavingHorizon] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [showConfidenceInfo, setShowConfidenceInfo] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (user) setHorizon(user.horizon_goal || '');
  }, [user]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [glimmerRes, themeRes] = await Promise.all([
        api.get('/echo-map/glimmers'),
        api.get('/echo-map/themes'),
      ]);
      setGlimmers(glimmerRes.data);
      setThemes(themeRes.data?.themes || []);
    } catch {
      toast.error('Could not load your tracker');
    } finally {
      setLoading(false);
    }
  };

  const loadGlimmers = loadAll; // alias for backwards compatibility

  const submitGlimmer = async () => {
    if (!event.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/echo-map/glimmers', { glimmer_event: event.trim(), certainty_score: score });
      setEvent('');
      setScore(7);
      setShowLog(false);
      await loadGlimmers();
      toast.success('Sign added to your map');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not log sign');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteGlimmer = async (id) => {
    try {
      await api.delete(`/echo-map/glimmers/${id}`);
      setSelected(null);
      await loadGlimmers();
    } catch {
      toast.error('Could not delete sign');
    }
  };

  const saveHorizon = async () => {
    if (!horizon.trim()) return;
    setSavingHorizon(true);
    try {
      await api.put('/auth/horizon', { horizon_goal: horizon.trim() });
      toast.success('Goal saved');
      setEditingHorizon(false);
      await checkAuth();
    } catch {
      toast.error('Could not save goal');
    } finally {
      setSavingHorizon(false);
    }
  };

  const stats = useMemo(() => {
    const total = glimmers.length;
    const avgScore = total ? (glimmers.reduce((s, g) => s + (g.certainty_score || 0), 0) / total).toFixed(1) : 0;
    const last = glimmers[0];
    return { total, avgScore, last };
  }, [glimmers]);

  if (loading) return <EchoMapSkeleton />;

  return (
    <div className="space-y-10" data-testid="echo-map-page">
      {/* Header */}
      <div className="animate-fade-up">
        <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-2">Reality Tracker</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight mystical-heading">Your Evidence Tracker</h1>
        <p className="text-text-muted font-sans text-base mt-3 max-w-xl leading-relaxed">
          Every sign is a small spark of change — a piece of evidence that your new self is taking root in real life. Log them as they happen, and a map of your becoming builds toward your goal.
        </p>
      </div>

      {/* Horizon */}
      <div className="mystical-card p-8 animate-fade-up delay-100" data-testid="horizon-section">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} className="text-gold" />
          <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Your Goal</span>
        </div>
        {editingHorizon || !user?.horizon_goal ? (
          <div className="space-y-3">
            <Textarea
              value={horizon}
              onChange={(e) => setHorizon(e.target.value)}
              data-testid="horizon-input"
              placeholder="Where are you headed? Write it in present tense, as if it's already true. e.g., 'I'm grounded in trust, doing work I love, with people who choose me back.'"
              rows={3}
              className="bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans leading-relaxed resize-none"
            />
            <div className="flex gap-2 justify-end">
              {user?.horizon_goal && (
                <Button
                  onClick={() => { setHorizon(user.horizon_goal); setEditingHorizon(false); }}
                  variant="ghost"
                  data-testid="horizon-cancel"
                  className="h-10 text-text-muted font-sans text-xs"
                >
                  Cancel
                </Button>
              )}
              <Button
                onClick={saveHorizon}
                disabled={savingHorizon || !horizon.trim()}
                data-testid="save-horizon-button"
                className="h-10 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-wider uppercase btn-hover"
              >
                {savingHorizon ? 'Saving...' : 'Save Goal'}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="font-serif text-xl sm:text-2xl text-dark italic leading-relaxed" data-testid="horizon-display">
              "{user.horizon_goal}"
            </p>
            <button
              onClick={() => setEditingHorizon(true)}
              data-testid="edit-horizon-button"
              className="mt-4 text-gold font-sans text-xs tracking-wider uppercase hover:text-gold/70"
              style={{ transition: 'color 0.2s ease' }}
            >
              Refine
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 animate-fade-up delay-200">
        <div className="mystical-card p-5 text-center" data-testid="stat-total">
          <p className="font-serif text-3xl text-dark">{stats.total}</p>
          <p className="font-sans text-[10px] text-text-muted tracking-[0.2em] uppercase mt-1">Signs</p>
        </div>
        <div className="mystical-card p-5 text-center relative" data-testid="stat-confidence">
          <button
            onClick={() => setShowConfidenceInfo(v => !v)}
            data-testid="confidence-info-button"
            className="absolute top-2 right-2 text-text-muted/70 hover:text-gold"
            style={{ transition: 'color 0.2s ease' }}
            aria-label="What is confidence?"
          >
            <Info size={12} />
          </button>
          <p className="font-serif text-3xl text-dark">{stats.avgScore}</p>
          <p className="font-sans text-[10px] text-text-muted tracking-[0.2em] uppercase mt-1">Confidence</p>
          {showConfidenceInfo && (
            <div
              data-testid="confidence-info-popover"
              className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 z-20 bg-dark text-bone rounded-xl p-4 font-sans text-[11px] leading-relaxed text-left shadow-xl animate-fade-up"
              style={{ boxShadow: '0 12px 32px rgba(0,0,0,0.25)' }}
            >
              <p className="font-serif text-sm text-gold mb-1">Confidence</p>
              <p>When you log a Sign, you rate how strongly it feels like evidence (1–10). This is the average across all your signs.</p>
              <p className="mt-2 text-bone/70">Higher = you're trusting the signs more. Lower = still questioning. Both are welcome.</p>
            </div>
          )}
        </div>
        <div className="mystical-card p-5 text-center">
          <p className="font-serif text-2xl text-dark">{stats.last ? new Date(stats.last.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}</p>
          <p className="font-sans text-[10px] text-text-muted tracking-[0.2em] uppercase mt-1">Last Sign</p>
        </div>
      </div>

      {/* Milky Way — themes as star clusters along a luminous band */}
      <div className="mystical-card relative overflow-hidden animate-fade-up delay-300" data-testid="constellation" style={{ minHeight: 460 }}>
        {/* Deep night-sky gradient */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 30% 20%, rgba(31,33,32,0.10), transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(198,178,143,0.06), transparent 55%)' }} />

        {/* Milky Way dust band (soft luminous S-curve behind the stars) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
          <defs>
            <linearGradient id="milkywayGradient" x1="0" y1="0" x2="100" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#C6B28F" stopOpacity="0" />
              <stop offset="30%" stopColor="#C6B28F" stopOpacity="0.18" />
              <stop offset="70%" stopColor="#C6B28F" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#C6B28F" stopOpacity="0" />
            </linearGradient>
            <filter id="milkywayBlur" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.5" />
            </filter>
          </defs>
          {/* Wide translucent band */}
          <path
            d="M 0,60 C 25,45 45,35 55,42 C 68,52 82,68 100,58"
            stroke="url(#milkywayGradient)"
            strokeWidth="14"
            fill="none"
            filter="url(#milkywayBlur)"
            opacity="0.7"
          />
          {/* Thin luminous spine */}
          <path
            d="M 0,60 C 25,45 45,35 55,42 C 68,52 82,68 100,58"
            stroke="#C6B28F"
            strokeWidth="0.4"
            fill="none"
            opacity="0.35"
          />
        </svg>

        {/* Distant background starfield */}
        {Array.from({ length: 80 }).map((_, i) => (
          <div
            key={`bg-${i}`}
            className="absolute rounded-full bg-text-muted"
            style={{
              left: `${(i * 13) % 100}%`,
              top: `${(i * 29) % 100}%`,
              width: 1.2, height: 1.2,
              opacity: 0.12 + ((i * 7) % 5) / 18,
            }}
          />
        ))}

        {/* Theme stars — the real content of the map */}
        {themes.map((t, idx) => {
          const pos = milkyWayPositionFor(idx, themes.length, t.theme);
          // Size scales with count. Base 10px, grows to ~30px for very frequent themes.
          const size = 10 + Math.min(20, t.count * 3);
          // Glow scales with brightness (0-100).
          const glowRadius = size * (1.2 + t.brightness / 120);
          const opacity = 0.55 + (t.brightness / 100) * 0.45;
          return (
            <button
              key={t.theme}
              onClick={() => setSelectedTheme(t)}
              data-testid={`theme-star-${idx}`}
              className="absolute group"
              style={{
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: 'translate(-50%, -50%)',
              }}
              aria-label={`${t.theme} — ${t.count} sign${t.count === 1 ? '' : 's'}`}
            >
              {/* Star glow */}
              <div
                className="relative rounded-full bg-gold animate-breathe"
                style={{
                  width: size,
                  height: size,
                  opacity,
                  boxShadow: `0 0 ${glowRadius}px rgba(198, 178, 143, ${0.35 + (t.brightness / 100) * 0.5})`,
                  animationDelay: `${idx * 0.18}s`,
                }}
              />
              {/* Theme label under each star */}
              <div
                className="absolute left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap font-sans text-[10px] tracking-wide text-text-muted group-hover:text-dark"
                style={{ top: '100%', transition: 'color 0.2s ease' }}
              >
                {t.theme}
                <span className="ml-1 text-text-muted/70">·{t.count}</span>
              </div>
            </button>
          );
        })}

        {/* Empty state */}
        {!loading && themes.length === 0 && glimmers.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8" data-testid="constellation-empty">
            <Star size={32} className="text-gold/50 mb-4" />
            <p className="font-serif text-xl text-dark mb-2">Your night sky is empty</p>
            <p className="font-sans text-sm text-text-muted leading-relaxed mb-2 max-w-sm">
              Log your first sign and a star will appear. As themes repeat, the stars grow — brighter themes hold the strongest evidence.
            </p>
          </div>
        )}

        {/* Explainer strip (bottom-left) */}
        {themes.length > 0 && (
          <div className="absolute bottom-4 left-4 max-w-xs pointer-events-none" data-testid="milkyway-legend">
            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-1">Your Milky Way</p>
            <p className="font-sans text-[11px] text-text-muted leading-relaxed">
              Each star is a theme. It grows brighter as similar signs repeat — frequency is your confidence.
            </p>
          </div>
        )}

        {/* Log button */}
        <button
          onClick={() => setShowLog(true)}
          data-testid="log-glimmer-button"
          className="absolute bottom-6 right-6 h-12 px-5 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.2em] uppercase flex items-center gap-2 btn-hover"
        >
          <Plus size={14} /> Log a Sign
        </button>
      </div>

      {/* Recent Signs list */}
      {glimmers.length > 0 && (
        <div className="mystical-card p-8 animate-fade-up delay-400" data-testid="glimmer-list-section">
          <p className="text-gold font-sans text-xs tracking-[0.25em] uppercase mb-5">Recent Signs</p>
          <div className="space-y-3">
            {glimmers.slice(0, 10).map((g, i) => (
              <div
                key={g.glimmer_id}
                data-testid={`glimmer-item-${i}`}
                className="flex items-start gap-4 p-4 rounded-xl bg-bone/40 border border-black/8 hover:border-gold/25"
                style={{ transition: 'border-color 0.2s ease' }}
              >
                <div className="flex flex-col items-center pt-1">
                  <div
                    className="rounded-full bg-gold"
                    style={{ width: 8, height: 8, boxShadow: '0 0 12px rgba(198,178,143,0.7)' }}
                  />
                  <p className="font-serif text-xs text-dark mt-1">{g.certainty_score}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm text-dark leading-relaxed">{g.glimmer_event}</p>
                  <p className="font-sans text-[11px] text-text-muted mt-1">
                    {new Date(g.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <button
                  onClick={() => deleteGlimmer(g.glimmer_id)}
                  data-testid={`delete-glimmer-${i}`}
                  className="text-text-muted/60 hover:text-red-500 p-2"
                  style={{ transition: 'color 0.2s ease' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Log Glimmer Modal */}
      {showLog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          data-testid="log-glimmer-modal"
          style={{ background: 'rgba(15,16,16,0.6)', backdropFilter: 'blur(8px)' }}
          onClick={() => setShowLog(false)}
        >
          <div
            className="bg-bone rounded-3xl p-8 max-w-lg w-full relative animate-fade-up"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}
          >
            <button
              onClick={() => setShowLog(false)}
              className="absolute top-5 right-5 text-text-muted hover:text-dark"
              data-testid="log-glimmer-close"
            >
              <X size={20} />
            </button>
            <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">Log a Sign</p>
            <h3 className="font-serif text-3xl text-dark mb-2 mystical-heading">What did you notice?</h3>
            <p className="font-sans text-sm text-text-muted mb-6 leading-relaxed">
              A small spark of change — a synchronicity, an easy yes, a small piece of evidence that something is shifting in your favor.
            </p>

            <div className="space-y-5">
              <Textarea
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                data-testid="glimmer-event-input"
                placeholder="A stranger said something I needed to hear. An opportunity arrived easily. I caught myself responding from my new self..."
                rows={4}
                className="bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans leading-relaxed resize-none"
              />

              <div>
                <Label className="text-text-muted text-xs tracking-wider uppercase font-sans">
                  How strongly does this feel like evidence?
                </Label>
                <div className="mt-3 flex items-center gap-3">
                  <Input
                    type="range"
                    min={1}
                    max={10}
                    value={score}
                    onChange={(e) => setScore(parseInt(e.target.value))}
                    data-testid="glimmer-score-slider"
                    className="flex-1 accent-[#C6B28F]"
                  />
                  <p className="font-serif text-2xl text-dark w-10 text-center" data-testid="glimmer-score-display">{score}</p>
                </div>
              </div>

              <Button
                onClick={submitGlimmer}
                disabled={submitting || !event.trim()}
                data-testid="submit-glimmer-button"
                className="w-full h-12 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.25em] uppercase btn-hover"
              >
                {submitting ? 'Adding...' : 'Add to your Map'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Glimmer Detail */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          data-testid="glimmer-detail-modal"
          style={{ background: 'rgba(15,16,16,0.6)', backdropFilter: 'blur(8px)' }}
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-bone rounded-3xl p-8 max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelected(null)}
              className="absolute top-5 right-5 text-text-muted hover:text-dark"
            >
              <X size={20} />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="rounded-full bg-gold"
                style={{ width: 14, height: 14, boxShadow: '0 0 16px rgba(198,178,143,0.8)' }}
              />
              <p className="font-serif text-xl text-dark">Confidence {selected.certainty_score}/10</p>
            </div>
            <p className="font-sans text-base text-dark leading-relaxed mb-6">{selected.glimmer_event}</p>
            <p className="font-sans text-xs text-text-muted">
              {new Date(selected.created_at).toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Theme detail modal — tapped a star, see all signs in that theme */}
      {selectedTheme && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          data-testid="theme-detail-modal"
          style={{ background: 'rgba(15,16,16,0.6)', backdropFilter: 'blur(8px)' }}
          onClick={() => setSelectedTheme(null)}
        >
          <div
            className="bg-bone rounded-3xl p-8 max-w-lg w-full relative max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}
          >
            <button
              onClick={() => setSelectedTheme(null)}
              className="absolute top-5 right-5 text-text-muted hover:text-dark"
              data-testid="theme-detail-close"
            >
              <X size={20} />
            </button>
            <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-2">Theme</p>
            <h3 className="font-serif text-3xl text-dark mb-3 mystical-heading" data-testid="theme-detail-title">{selectedTheme.theme}</h3>
            <div className="flex items-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-gold" style={{ width: 10, height: 10, boxShadow: '0 0 12px rgba(198,178,143,0.7)' }} />
                <span className="font-serif text-lg text-dark">{selectedTheme.count}</span>
                <span className="font-sans text-xs text-text-muted">sign{selectedTheme.count === 1 ? '' : 's'}</span>
              </div>
              <div className="w-px h-4 bg-text-muted/30" />
              <div className="flex items-center gap-2">
                <span className="font-serif text-lg text-dark">{selectedTheme.avg_certainty}</span>
                <span className="font-sans text-xs text-text-muted">avg confidence</span>
              </div>
            </div>
            <div className="space-y-3">
              {glimmers
                .filter(g => selectedTheme.glimmer_ids.includes(g.glimmer_id))
                .map((g, i) => (
                  <div
                    key={g.glimmer_id}
                    data-testid={`theme-glimmer-${i}`}
                    className="p-4 rounded-xl bg-sand/40 border border-black/8"
                  >
                    <p className="font-sans text-sm text-dark leading-relaxed">{g.glimmer_event}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="font-sans text-[11px] text-text-muted">
                        {new Date(g.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                      <p className="font-serif text-xs text-gold">Confidence {g.certainty_score}/10</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
